import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, firstValueFrom, map, of, Subject, take, takeUntil, timeout } from 'rxjs';
import { BladeService } from 'src/app/modules/blades/services/blade.service';
import { DeepLinkService, OMSContextSyncService, SignInService } from 'src/app/modules/core/services';
import { WidgetsService } from '@components/widgets/widgets.service';
import { APP_CONTEXT } from 'src/app/modules/core/models/app-state';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { LineService } from 'src/app/modules/core/services/line.service';
import { MapService } from 'src/app/modules/core/services/map.service';
import { MapStateService } from 'src/app/modules/map/map-state.service';
import { VehicleStore } from 'src/app/modules/core/store';
import { HistoryScrubbarComponent } from '../../components/history/historyScrubbar/historyScrubbar.component';
import { MapComponent as MapComponent_1 } from '../../modules/map/map.component';
import { BladeContainerComponent } from '../../modules/blades/components/blade-container/blade-container.component';
import { BladeComponent } from '../../modules/blades/components/blade/blade.component';
import { SelectionOverviewComponent } from '../../components/selection-overview/selection-overview.component';

import { MatDialog } from '@angular/material/dialog';
import { LoggingHelperService } from '@modules/shared/helpers/logging-helper.service';

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    imports: [SelectionOverviewComponent, BladeContainerComponent, BladeComponent, MapComponent_1, HistoryScrubbarComponent],
    providers: [MatDialog],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly _bladeService = inject(BladeService);
  private readonly _lineService = inject(LineService);  
  private readonly _mapStateService = inject(MapStateService);  
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appStateService = inject(AppStateService);
  private readonly _mapService = inject(MapService); // needs to be injected to initialize
  private readonly _omsContextSyncService = inject(OMSContextSyncService); // needs to be injected to initialize
  private readonly _signInService = inject(SignInService);
  private readonly _vehicleStore = inject(VehicleStore);
  private readonly _deepLinkService = inject(DeepLinkService);
  private readonly _loggingHelperService = inject(LoggingHelperService);
  private readonly _widgetStateService = inject(WidgetsService);

  public leftPanelVisible: boolean = false;
  public left = '0px';
  private _bootAppliedKey: string | null = null;

  private readonly _ngUnsubscribe$ = new Subject<void>();

  ngOnInit() {
    this._bladeService.init(3);
    this._lineService.init();    

    this._appStateService.getPropertyState('leftTotalWidth').pipe(
      takeUntil(this._ngUnsubscribe$)
    ).subscribe(leftTotalWidth => {
        this.left = leftTotalWidth + 'px';
    });
    this._appStateService.getPropertyState('leftPanelIsVisible').pipe(
      takeUntil(this._ngUnsubscribe$)
    ).subscribe((value: boolean) => {
      this.leftPanelVisible = value;
    });

    // listen to mapItemClicked and change routing
    this._listenToMapItemClicked();

    this._loggingHelperService.logPageViewCount('Home', this._router.url);
  }

  async ngAfterViewInit() {
    // Wait until full app state has loaded (including persisted AppState) before handling deep links.
    // userHasLoaded$ can become true earlier and then AppState init may overwrite context afterwards.
    await firstValueFrom(this._signInService.stateHasLoaded$.pipe(filter((loaded) => loaded === true), take(1)));
    this._handleDeepLink();
  }

  ngOnDestroy() {
      this._ngUnsubscribe$.next();
      this._ngUnsubscribe$.complete();
  }

  private _listenToMapItemClicked() {
      this._mapStateService.getMapPropertyState('itemsClicked').pipe(
          takeUntil(this._ngUnsubscribe$)
        ).subscribe(itemsClicked => {
          if (itemsClicked && itemsClicked.length > 0) {
            const item = itemsClicked[0];
            if (item.type === 'vehicle' && item.id !== undefined) {
              this._navigateToItem(item.type, item.id);
            }
          }
        });
  }

  private _navigateToItem(type: string, id: any) {
    if (type === 'vehicle') {
      this._widgetStateService.openVehicle(String(id));
    } else {
      this._router.navigate(['', { outlets: { outlet1: [type, id], outlet2: null, 'outlet-right0': null, 'outlet-right1': null, 'outlet-right2': null }}]);
    }
  }

  private _handleDeepLink() {
    const initialParams = this._deepLinkService.getInitialDeepLinkParams(this._route);

    // No deep link params (user opened manually): reset to default map view.
    if (!initialParams) {
      this._appStateService.changeContext(APP_CONTEXT.NONE);
      this._router.navigate(['/'], { queryParamsHandling: '' });
    } else {
      if (initialParams.view === 'history' && initialParams.startDateTime && initialParams.endDateTime) {
        this._bootAppliedKey = this._deepLinkKey(initialParams.vehicle, initialParams.view, initialParams.startDateTime, initialParams.endDateTime);
      }
      this._applyDeepLink(initialParams.vehicle, initialParams.view, initialParams.startDateTime, initialParams.endDateTime);
    }

    // URL is source of truth for live updates.
    this._route.queryParamMap.pipe(
      map(params => ({
        vehicle: params.get('vehicle'),
        view: params.get('view'),
        startDateTime: params.get('startDateTime'),
        endDateTime: params.get('endDateTime')
      })),
      distinctUntilChanged((a, b) =>
        a.vehicle === b.vehicle
        && a.view === b.view
        && a.startDateTime === b.startDateTime
        && a.endDateTime === b.endDateTime),
      filter(p => !!p.vehicle),
      takeUntil(this._ngUnsubscribe$)
    ).subscribe(({ vehicle: vehicleNumber, view, startDateTime, endDateTime }) => {
      const key = this._deepLinkKey(vehicleNumber, view, startDateTime, endDateTime);
      if (this._bootAppliedKey && this._bootAppliedKey === key) {
        this._bootAppliedKey = null;
        return;
      }

      this._applyDeepLink(vehicleNumber, view, startDateTime, endDateTime);
    });
  }

  private _applyDeepLink(vehicleNumber: string, view: string | null, startDateTime: string | null, endDateTime: string | null) {
    const effectiveView = view ?? 'vehicle';
    if (effectiveView === 'history' && startDateTime && endDateTime) {
      this._processDeepLinkHistory(vehicleNumber, startDateTime, endDateTime);
      return;
    }
    this._processDeepLinkVehicle(vehicleNumber);
  }

  private _deepLinkKey(vehicle: string, view: string | null, startDateTime: string | null, endDateTime: string | null): string {
    return `${vehicle}|${view ?? ''}|${startDateTime ?? ''}|${endDateTime ?? ''}`;
  }

  private _processDeepLinkHistory(vehicleNumber: string, startDateTime: string, endDateTime: string) {
    // For history deep link: show the history panel and set outlet0:history (matches manual history search URL).
    // Restore query params so HistoryComponent can read them (needed when params came from storage after MSAL redirect).
    if (this._appStateService.stateSnapshot?.selectedContext !== APP_CONTEXT.HISTORY) {
      this._appStateService.changeContext(APP_CONTEXT.HISTORY);
    } else {
      // changeContext toggles same context to NONE; keep panel visible without toggling.
      this._appStateService.showSelectionPanel();
    }
    this._router.navigate(['', { outlets: { outlet0: ['history'], outlet1: null, outlet2: null } }], {
      queryParams: { vehicle: vehicleNumber, view: 'history', startDateTime, endDateTime },
      queryParamsHandling: ''
    });
  }

  private async _processDeepLinkVehicle(vehicleNumber: string) {
    // Vehicle deep links should not keep history (or any other left-panel) context visible.
    if (this._appStateService.stateSnapshot?.selectedContext !== APP_CONTEXT.NONE) {
      this._appStateService.changeContext(APP_CONTEXT.NONE, false);
    }

    // For vehicle deep links, wait for vehicle to be loaded in store before navigating
    // This ensures VehicleComponent can find the vehicle when it subscribes
    this._vehicleStore.selectVehicles([vehicleNumber], this._deepLinkService.vehicleSelectSource);
    
    try {
      await firstValueFrom(
        this._vehicleStore.getVehicleState(vehicleNumber).pipe(
          filter(vehicle => vehicle != undefined),
          take(1),
          timeout(this._deepLinkService.vehicleLoadTimeoutMs),
          catchError(() => {
            console.warn(`MapComponent: Vehicle not found in store after ${this._deepLinkService.vehicleLoadTimeoutMs}ms, proceeding anyway`);
            return of(undefined);
          })
        )
      );
    } catch (e) {
      console.warn('MapComponent: Error waiting for vehicle in store:', e);
    }

    // Deep-link behavior: open the vehicle on the right side, clearing left-side blades.
    this._router.navigate(['', { outlets: { outlet0: null, outlet1: null, outlet2: null, 'outlet-right0': ['vehicle', vehicleNumber], 'outlet-right1': null, 'outlet-right2': null } }], {
      queryParams: { vehicle: vehicleNumber, view: 'vehicle' },
      queryParamsHandling: ''
    });
  }
}
